import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  AtualizarBrandGuidelines,
  AtualizarCliente,
  AtualizarDossie,
  AtualizarVolumetria,
  CriarDataComemorativa,
} from "@base/contracts";
import { useAuth } from "../../../shared/auth/auth.contexto";
import { useClientesGateway } from "./clientes.contexto";

const chaveAtual = ["clientes", "atual"] as const;
const chaveLista = ["clientes", "lista"] as const;
const chaveBrand = ["clientes", "brand"] as const;
const chaveDatas = ["clientes", "datas"] as const;
const chaveVol = ["clientes", "volumetria"] as const;
const chaveDossie = ["clientes", "dossie"] as const;
const chaveVersoes = ["clientes", "dossie", "versoes"] as const;
const chaveMeta = ["clientes", "meta"] as const;
const chaveContasSociais = ["clientes", "contas-sociais"] as const;

export function useTemGerirClientes(): boolean {
  const { sessao } = useAuth();
  return sessao?.escopos.includes("clientes:gerir") ?? false;
}

export function useListarClientes(consulta?: {
  readonly q?: string;
  readonly status?: "ativo" | "inativo" | "todos";
}) {
  const gateway = useClientesGateway();
  return useQuery({
    queryKey: [...chaveLista, consulta?.q ?? "", consulta?.status ?? "ativo"],
    queryFn: () => gateway.listar(consulta),
  });
}

export function useAtualizarStatusCliente() {
  const gateway = useClientesGateway();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (entrada: {
      readonly clienteId: string;
      readonly status: "ativo" | "inativo";
    }) => gateway.atualizarStatus(entrada.clienteId, entrada.status),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: chaveLista });
      void qc.invalidateQueries({ queryKey: chaveAtual });
    },
  });
}

export function useClienteAtual() {
  const gateway = useClientesGateway();
  return useQuery({
    queryKey: chaveAtual,
    queryFn: () => gateway.obterAtual(),
  });
}

export function useAtualizarCliente() {
  const gateway = useClientesGateway();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (corpo: AtualizarCliente) => gateway.atualizarAtual(corpo),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: chaveAtual });
      void qc.invalidateQueries({ queryKey: chaveLista });
    },
  });
}

export function useBrandGuidelines() {
  const gateway = useClientesGateway();
  return useQuery({
    queryKey: chaveBrand,
    queryFn: () => gateway.obterBrand(),
  });
}

export function useAtualizarBrand() {
  const gateway = useClientesGateway();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (corpo: AtualizarBrandGuidelines) => gateway.atualizarBrand(corpo),
    onSuccess: () => void qc.invalidateQueries({ queryKey: chaveBrand }),
  });
}

export function useDatasComemorativas(mes?: number) {
  const gateway = useClientesGateway();
  return useQuery({
    queryKey: [...chaveDatas, mes ?? "todos"],
    queryFn: () => gateway.listarDatas(mes),
  });
}

export function useCriarData() {
  const gateway = useClientesGateway();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (corpo: CriarDataComemorativa) => gateway.criarData(corpo),
    onSuccess: () => void qc.invalidateQueries({ queryKey: chaveDatas }),
  });
}

export function useRemoverData() {
  const gateway = useClientesGateway();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => gateway.removerData(id),
    onSuccess: () => void qc.invalidateQueries({ queryKey: chaveDatas }),
  });
}

export function useVolumetria() {
  const gateway = useClientesGateway();
  return useQuery({
    queryKey: chaveVol,
    queryFn: () => gateway.obterVolumetria(),
  });
}

export function useAtualizarVolumetria() {
  const gateway = useClientesGateway();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (corpo: AtualizarVolumetria) =>
      gateway.atualizarVolumetria(corpo),
    onSuccess: () => void qc.invalidateQueries({ queryKey: chaveVol }),
  });
}

export function useDossie() {
  const gateway = useClientesGateway();
  return useQuery({
    queryKey: chaveDossie,
    queryFn: () => gateway.obterDossie(),
  });
}

export function useAtualizarDossie() {
  const gateway = useClientesGateway();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (corpo: AtualizarDossie) => gateway.atualizarDossie(corpo),
    onSuccess: () => void qc.invalidateQueries({ queryKey: chaveDossie }),
  });
}

export function useVersoesDossie() {
  const gateway = useClientesGateway();
  return useQuery({
    queryKey: chaveVersoes,
    queryFn: () => gateway.listarVersoesDossie(),
  });
}

export function useSalvarVersaoDossie() {
  const gateway = useClientesGateway();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => gateway.salvarVersaoDossie(),
    onSuccess: () => void qc.invalidateQueries({ queryKey: chaveVersoes }),
  });
}

export function useRestaurarVersaoDossie() {
  const gateway = useClientesGateway();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => gateway.restaurarVersaoDossie(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: chaveDossie });
      void qc.invalidateQueries({ queryKey: chaveVersoes });
    },
  });
}

export function useMetaConexao() {
  const gateway = useClientesGateway();
  return useQuery({
    queryKey: chaveMeta,
    queryFn: async () => {
      const [conexao, health] = await Promise.all([
        gateway.obterMetaConexao(),
        gateway.obterMetaHealth(),
      ]);
      return { conexao, health };
    },
  });
}

export function useContasSociais() {
  const gateway = useClientesGateway();
  return useQuery({
    queryKey: chaveContasSociais,
    queryFn: () => gateway.listarContasSociais(),
  });
}
